import { useHistory } from 'react-router';
import { routes } from '@/constant/routes.const';
import styled, { keyframes } from 'styled-components';
import FlexWrap from '@/components/common/FlexWrap';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import ProgressBar from './ProgressBar';
import { IGlobalState } from '@/store';
import TextButton from '../common/TextButton';
import { getMedicalStatusData } from '@/constant/refundStatus.const';
import { Button } from '@successmode-ktp/kui';
import { BankCallGuideButton, CashPickupGuideButton } from '@repo/components';
import { ICON_LOCATION_BLUE } from '@repo/assets';

function CurrentStatus() {
  const {
    isRefundInfoRegistered,
    paymentStatus,
    nationality,
    departureConfirmDate,
    departureProgress,
    refundedIn,
    refundedDate,
    isCashPickup,
    isUploadRequired,
  } = useAppSelector((state) => state) as IGlobalState;
  const { title, description, image, warning } = getMedicalStatusData({
    paymentStatus,
    isRefundInfoRegistered,
    nationality,
    departureConfirmDate,
    departureProgress,
    refundedIn,
    refundedDate,
    isCashPickup,
    isUploadRequired,
  });
  const history = useHistory();
  const dispatch = useAppDispatch();

  const isVisibleBankInquiryGuide =
    refundedIn === 'UNKNOWN' &&
    (nationality === 'JPN' || nationality === 'CHN');
  const isVisibleInputInfo =
    !isRefundInfoRegistered && refundedIn === 'UNKNOWN';

  const openModal = (e: any) => {
    e.stopPropagation();
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'BANK_INQUIRY_GUIDE',
        isActive: true,
      },
    });
  };

  const openCashPickupModal = (e: any) => {
    e.stopPropagation();
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'CASH_PICKUP_GUIDE',
        isActive: true,
      },
    });
  };

  return (
    <>
      <Container
        // onClick={onClickActionBtn}
        isRefundInfoRegistered={isRefundInfoRegistered}
      >
        <ProgressBar />
        <FlexWrap
          gap='9px'
          alignItems='center'
          justifyContent='space-between'
          style={{ width: '100%', marginTop: 24 }}
        >
          <FlexWrap
            alignItems='center'
            justifyContent='space-between'
            style={{ width: '100%' }}
          >
            <div>
              <Title>{title}</Title>
              <Description>{description}</Description>
              <Warning>{warning}</Warning>
            </div>
            <StatusImg
              src={image}
              isAirportComplete={refundedIn === 'UNKNOWN'}
            />
          </FlexWrap>
        </FlexWrap>
        {isCashPickup && (
          <CashPickupGuideButton onClick={openCashPickupModal} />
        )}
        {isVisibleBankInquiryGuide && (
          <BankCallGuideButton onClick={openModal} />
        )}
        {isVisibleInputInfo && (
          <>
            <TextButton
              title={(() => {
                if (nationality === 'KOR') {
                  return '계좌 정보 입력';
                }
                if (isCashPickup) {
                  return 'Enter Personal information';
                }
                return 'Enter the information';
              })()}
              onClick={() => history.push(routes.medicalCustomerRefund)}
              color='#fff'
              width='100%'
              fontSize='16px'
              margin='8px 0 8px 0'
            />
          </>
        )}
        {isCashPickup &&
          isRefundInfoRegistered &&
          paymentStatus !== 'PAYMENT_COMPLETE' &&
          !isUploadRequired && (
            <>
              <TextButton
                title={'View Entered Information'}
                onClick={() => history.push(routes.medicalRefundInfo)}
                color='#246CF6'
                width='100%'
                fontSize='16px'
                margin='8px 0 0 0'
                bgColor='#E9F0FE'
              />
              <TextButton
                title={'View Tax refund Receipt'}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  history.push(routes.medicalReceipt);
                }}
                color='#fff'
                width='100%'
                fontSize='16px'
                margin='8px 0 8px 0'
              />
            </>
          )}
        {paymentStatus === 'PAYMENT_COMPLETE' && isCashPickup && (
          <TextButton
            title={'View Western Union Locations'}
            icon={ICON_LOCATION_BLUE}
            onClick={() =>
              window.open(
                'https://www.westernunion.com/global-services/find-locations?WUCountry=us&WULanguage=en',
                '_blank',
              )
            }
            color='#246CF6'
            width='100%'
            fontSize='16px'
            margin='8px 0 8px 0'
            bgColor='#E9F0FE'
          />
        )}

        {departureProgress === 'NOT_YET_DEPARTURE' &&
          nationality === 'KOR' &&
          isRefundInfoRegistered && (
            <ButtonWrapper>
              <Button
                type='button'
                theme='mono'
                size='lg'
                style={{ backgroundColor: '#E9F0FE', color: '#246cf6' }}
                onClick={(e) => {
                  e!.stopPropagation();
                  history.push(routes.uploadKorFileGuide);
                }}
              >
                출입국사실증명서 다운로드 방법
              </Button>
              <Button
                type='button'
                size='lg'
                onClick={(e) => {
                  e!.stopPropagation();
                  history.push(routes.uploadKorFile);
                }}
              >
                출입국사실증명서 업로드하기
              </Button>
            </ButtonWrapper>
          )}
        {departureProgress === 'NOT_YET_DEPARTURE' &&
          isUploadRequired &&
          isRefundInfoRegistered && (
            <ButtonWrapper>
              <Button
                type='button'
                size='lg'
                onClick={(e) => {
                  e!.stopPropagation();
                  history.push(routes.medicalUploadFile);
                }}
              >
                Upload files
              </Button>
            </ButtonWrapper>
          )}
        {departureProgress === 'DEPARTURE_CHECK_COMPLETE' &&
          paymentStatus !== 'PAYMENT_COMPLETE' && (
            <ButtonWrapper>
              <Button
                type='button'
                theme='mono'
                size='lg'
                style={{ backgroundColor: '#E9F0FE', color: '#246cf6' }}
                onClick={(e) => {
                  e!.stopPropagation();
                  history.push(routes.medicalReceipt);
                }}
              >
                View Tax Refund Receipt
              </Button>
            </ButtonWrapper>
          )}
        {departureProgress === 'TICKET_RE_UPLOAD' && nationality === 'KOR' && (
          <ButtonWrapper>
            <Button
              type='button'
              theme='mono'
              size='lg'
              style={{ backgroundColor: '#E9F0FE', color: '#246cf6' }}
              onClick={(e) => {
                e!.stopPropagation();
                history.push(routes.uploadKorFileGuide);
              }}
            >
              출입국사실증명서 다운로드 방법
            </Button>
            <Button
              type='button'
              size='lg'
              onClick={(e) => {
                e!.stopPropagation();
                history.push(routes.uploadKorFile);
              }}
            >
              출입국사실증명서 재업로드하기
            </Button>
          </ButtonWrapper>
        )}
        {departureProgress === 'TICKET_RE_UPLOAD' && nationality !== 'KOR' && (
          <ButtonWrapper>
            <Button
              type='button'
              size='lg'
              variant='line'
              onClick={(e) => {
                e!.stopPropagation();
                history.push(routes.medicalUploadFile);
              }}
            >
              Reupload documents
            </Button>
          </ButtonWrapper>
        )}
      </Container>
    </>
  );
}
const blinkAnimation = keyframes`
0% {opacity:1;}
50% {opacity:0.6;}
100% {opacity:1;}
`;
const Container = styled.div<{ isRefundInfoRegistered: boolean }>`
  margin: 24px 0;
  padding: 20px;
  width: 100%;
  border-radius: 20px;
  box-shadow: 0px 0px 20px 0px rgba(174, 191, 204, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  /* cursor: pointer; */
  animation: ${blinkAnimation} 1500ms infinite;
  animation-play-state: ${(props) =>
    props.isRefundInfoRegistered ? 'paused' : 'running'};
`;

const Title = styled.h3`
  font-size: 18px;
  line-height: 150%;
  font-weight: 700;
  color: #111;
  white-space: pre-wrap;
`;
const Description = styled.p`
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 150%;
  color: #3a3b3e;
  margin: 8px 0 0;
  word-break: keep-all;
`;
const Warning = styled.small`
  display: block;
  margin-top: 8px;
  color: var(--KTP-Warning-100, #ed0828);
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.24px;
`;
const StatusImg = styled.img<{ isAirportComplete: boolean }>`
  width: 60px;
  height: auto;
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 28px;
`;

export default CurrentStatus;
